/**
 * @file DropdownSelection.tsx
 *
 * Generic chip-input field with a click-outside dropdown panel, mirroring WSB-v2's
 * `DropdownSelection`. Selected items render as removable Gravity `Chip`s inside a
 * `gv-input gv-with-addon` box with a trailing search icon; a bare text input
 * filters via `searchFn`, and clicking the field opens the panel rendered by
 * `renderDropdown`. Edits are staged in a draft selection that the panel's Save
 * commits (`onSave`) and Cancel — or a click outside — reverts. The caller owns the
 * data shape; this only manages open/draft/query state. Chips wrap rather than
 * collapsing behind a "+N more" control (WSB-v2's ResizeObserver measurement is
 * intentionally omitted, since it is inline-style-heavy and jsdom-hostile).
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  type SyntheticEvent,
} from 'react';
import { Chip, Icon } from '@group.one/react-gravity';
import styles from './DropdownSelection.module.css';

interface DropdownSelectionProps<TSelection, TDropdownData> {
  /** The committed selection (chips reflect a working copy of this). */
  selected: TSelection[];
  /** Renders a selection's chip label. */
  getLabel: (selection: TSelection) => string;
  /** Called with the draft selection when the panel's Save fires. */
  onSave: (selected: TSelection[]) => void;
  /** Maps the current query to the data passed to `renderDropdown`. */
  searchFn: (query: string) => TDropdownData[];
  /** Renders the dropdown panel; given the search results and draft controls. */
  renderDropdown: (
    data: TDropdownData[],
    query: string,
    clearQuery: () => void,
    selected: TSelection[],
    setSelected: Dispatch<SetStateAction<TSelection[]>>,
    save: (event?: SyntheticEvent) => void,
    cancel: (event?: SyntheticEvent) => void,
  ) => ReactNode;
  /** aria-label + placeholder for the inline search input. */
  searchLabel?: string;
  disabled?: boolean;
  testIdPrefix?: string;
}

/**
 * Renders the chip-input field and its dropdown panel.
 *
 * @param props - The selection, label/search/save callbacks, and the panel renderer.
 *
 * @returns The dropdown-selection control.
 */
export function DropdownSelection<TSelection, TDropdownData>({
  selected,
  getLabel,
  onSave,
  searchFn,
  renderDropdown,
  searchLabel,
  disabled = false,
  testIdPrefix,
}: DropdownSelectionProps<TSelection, TDropdownData>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draftSelected, setDraftSelected] = useState(selected);
  const containerRef = useRef<HTMLDivElement>(null);

  const clearQuery = useCallback(() => setQuery(''), []);

  const cancel = useCallback(
    (event?: SyntheticEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      setDraftSelected(selected);
      setQuery('');
      setOpen(false);
    },
    [selected],
  );

  const save = useCallback(
    (event?: SyntheticEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      onSave(draftSelected);
      setQuery('');
      setOpen(false);
    },
    [draftSelected, onSave],
  );

  // Re-sync the draft whenever the committed selection changes upstream.
  useEffect(() => {
    setDraftSelected(selected);
  }, [selected]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  // A pointer-down outside the control cancels (reverts the draft) and closes it.
  useEffect(() => {
    if (!open || disabled) {
      return;
    }
    const handleMouseDown = (event: MouseEvent): void => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }
      cancel();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [cancel, disabled, open]);

  const dropdownData = searchFn(query);

  return (
    <div
      ref={containerRef}
      className="gv-pos-relative"
      data-testid={testIdPrefix}
      onMouseDown={() => {
        if (!disabled) {
          setOpen(true);
        }
      }}
    >
      <div className={`gv-input gv-with-addon ${styles.field}`}>
        <div className="gv-flex gv-flex-wrap gv-items-center gv-w-full gv-py-sm gv-px-md gv-gap-sm">
          {draftSelected.map((selection, index) => (
            <Chip
              key={`${getLabel(selection)}-${index}`}
              active
              className="gv-flex-shrink-0"
              onClick={() => {
                if (!disabled) {
                  setDraftSelected((current) =>
                    current.filter((item) => item !== selection),
                  );
                }
              }}
            >
              {getLabel(selection)}
            </Chip>
          ))}
          {/* Inline styles beat Gravity's `.gv-input input { all: unset; width: 100% }` reset, which would otherwise force the input full-width and wrap it below the chips. */}
          <input
            type="text"
            className="gv-p-0 gv-text-sm"
            style={{ flex: '1 0 6rem', width: 'auto', minWidth: '6rem' }}
            value={query}
            disabled={disabled}
            placeholder={draftSelected.length === 0 ? searchLabel : undefined}
            aria-label={searchLabel}
            data-testid={testIdPrefix ? `${testIdPrefix}-search` : undefined}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <div className={`gv-flex gv-items-center gv-border-l gv-px-md ${styles.addon}`}>
          <Icon name="search" />
        </div>
      </div>

      {open && !disabled && (
        <div
          className={`gv-pos-absolute gv-w-full gv-border gv-radius gv-surface-bright ${styles.panel}`}
        >
          {renderDropdown(
            dropdownData,
            query,
            clearQuery,
            draftSelected,
            setDraftSelected,
            save,
            cancel,
          )}
        </div>
      )}
    </div>
  );
}
